import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';
import '../styles/fees-page.css';

const FeesDocumentation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="therapy-site" data-testid="fees-documentation-page">
      <Helmet>
        <title>Fees & Documentation | Inspire Therapy Collective - Orlando, FL</title>
        <meta name="description" content="Transparent pricing for documentation services including court reports, workplace letters, school accommodations, and medical summaries at Inspire Therapy Collective." />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="fees-hero">
        <div className="fees-hero-content fade-in-up">
          <h1 className="fees-page-title gradient-text" data-testid="fees-page-title">Fees & Documentation</h1>
          <p className="fees-subtitle">Transparent pricing for the services that support your care.</p>
          <p className="fees-intro-text">
            I believe you should never be surprised by a bill. Below you'll find fee information for documentation services — letters, reports, and forms that support your work, school, legal, or medical needs. If you have questions about what's covered by your insurance or what to expect before your first session, reach out directly and I'll walk you through it.
          </p>
        </div>
      </section>

      {/* Session Fees */}
      <section className="fees-section">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">Session Fees</h2>
          <p className="fees-section-intro fade-in-up" style={{ fontStyle: 'italic' }}>Investing in your wellbeing.</p>
          <p className="fees-intro-body fade-in-up">
            I believe access to quality care should feel clear and straightforward. Individual sessions are $150 and couples sessions are $175. A sliding scale is available — reach out if you'd like to explore eligibility.
          </p>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="session-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Individual Session</td><td>$150</td></tr>
                <tr><td>Couples Session</td><td>$175</td></tr>
              </tbody>
            </table>
          </div>
          <p className="fees-sliding-note fade-in-up">Sliding scale available — contact me to learn more.</p>
          <div className="fees-insurance-link-wrapper fade-in-up">
            <a href="/#insurance" className="fees-insurance-pill" data-testid="view-insurance-link" onClick={(e) => { e.preventDefault(); navigate('/', { state: { scrollTo: 'insurance' } }); }}>
              View Accepted Insurance Plans &rarr;
            </a>
          </div>
          <p className="fees-payment-methods fade-in-up">I accept American Express, Apple Cash, Cash, Discover, Mastercard, Venmo, Visa, and Zelle.</p>
        </div>
      </section>

      {/* Divider */}
      <div className="fees-divider">
        <div className="fees-divider-line" />
        <span className="fees-divider-label">Documentation Services</span>
        <div className="fees-divider-line" />
      </div>

      {/* Section 1: Court-Related */}
      <section className="fees-section fees-section-alt">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">Court-Related Documentation</h2>
          <p className="fees-section-intro fade-in-up">Forensic-level documentation requiring detailed clinical analysis and legal-appropriate formatting.</p>
          <div className="fees-services-list fade-in-up">
            <span>Court-ready clinical evaluation reports</span>
            <span>Mental health summaries for legal proceedings</span>
            <span>Diagnostic clarification (DSM-5-TR)</span>
            <span>Functional impairment statements</span>
            <span>Treatment progress summaries</span>
            <span>Letters for attorneys, GALs, or case managers</span>
          </div>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="court-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Court-Ready Evaluation Report</td><td>$750</td></tr>
                <tr><td>Attorney Communication</td><td>$200/hour</td></tr>
                <tr><td>Record Review (beyond 30 min)</td><td>$150/hour</td></tr>
                <tr><td>Court Appearance / Testimony</td><td>$200/hour</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 2: Workplace */}
      <section className="fees-section">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">Workplace & Employment Documentation</h2>
          <p className="fees-section-intro fade-in-up">Clinically appropriate documentation supporting mental health needs in the workplace.</p>
          <div className="fees-services-list fade-in-up">
            <span>FMLA forms</span>
            <span>Medical leave extensions</span>
            <span>Return-to-work recommendations</span>
            <span>Workplace accommodation letters</span>
            <span>Short-term disability documentation</span>
            <span>Fitness-for-duty clinical summaries (non-forensic)</span>
          </div>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="workplace-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Form Completion</td><td>$150–$300</td></tr>
                <tr><td>Detailed Clinical Summary</td><td>$150–$250</td></tr>
                <tr><td>Basic Letter</td><td>$50–$100</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 3: School & Academic */}
      <section className="fees-section fees-section-alt">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">School & Academic Documentation</h2>
          <p className="fees-section-intro fade-in-up">Support for students, parents, and educational teams.</p>
          <div className="fees-services-list fade-in-up">
            <span>504/IEP support letters</span>
            <span>School accommodation recommendations</span>
            <span>Attendance support documentation</span>
            <span>Behavioral/emotional functioning summaries</span>
            <span>Letters supporting testing accommodations</span>
          </div>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="school-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Basic Letter</td><td>$50–$100</td></tr>
                <tr><td>Detailed Academic Summary</td><td>$150–$250</td></tr>
                <tr><td>Form Completion</td><td>$150–$300</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 4: Medical & Wellness */}
      <section className="fees-section">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">Medical & Wellness Documentation</h2>
          <p className="fees-section-intro fade-in-up">Collaborative documentation for your medical providers and care team.</p>
          <div className="fees-services-list fade-in-up">
            <span>Mental health summaries for primary care or specialists</span>
            <span>Documentation supporting medication management</span>
            <span>Treatment coordination letters</span>
            <span>Referral letters to psychiatry, neurology, or specialty care</span>
          </div>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="medical-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Basic Letter</td><td>$50–$100</td></tr>
                <tr><td>Detailed Clinical Summary</td><td>$150–$250</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 5: Insurance & Administrative */}
      <section className="fees-section fees-section-alt">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">Insurance & Administrative Documentation</h2>
          <p className="fees-section-intro fade-in-up">Factual, concise documentation for administrative or insurance needs.</p>
          <div className="fees-services-list fade-in-up">
            <span>Clinical summaries for insurance appeals</span>
            <span>Treatment verification letters</span>
            <span>Attendance verification</span>
            <span>Progress summaries for case managers</span>
          </div>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table" data-testid="insurance-admin-fees-table">
              <thead>
                <tr><th>Service</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Basic Letter</td><td>$50–$100</td></tr>
                <tr><td>Detailed Summary</td><td>$150–$250</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Section 6: Turnaround Times */}
      <section className="fees-section">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">How Quickly Can I Get My Documentation?</h2>
          
          <h3 className="fees-subsection-heading fade-in-up">General Documentation</h3>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table fees-table-turnaround" data-testid="general-turnaround-table">
              <thead>
                <tr><th>Tier</th><th>Timeframe</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Standard</td><td>7–10 business days</td><td>Included</td></tr>
                <tr><td>Rush</td><td>3–6 business days</td><td>+$75</td></tr>
                <tr><td>Expedited</td><td>48–72 hours</td><td>+$150</td></tr>
                <tr><td>Urgent</td><td>Same / next business day</td><td>+$200–$300</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="fees-subsection-heading fade-in-up">Court-Related Documentation</h3>
          <div className="fees-table-wrapper fade-in-up">
            <table className="fees-table fees-table-turnaround" data-testid="court-turnaround-table">
              <thead>
                <tr><th>Tier</th><th>Timeframe</th><th>Fee</th></tr>
              </thead>
              <tbody>
                <tr><td>Standard</td><td>7–14 business days</td><td>Included</td></tr>
                <tr><td>Rush</td><td>3–6 business days</td><td>+$150</td></tr>
                <tr><td>Expedited</td><td>48–72 hours</td><td>+$250</td></tr>
                <tr><td>Urgent</td><td>Same / next business day</td><td>+$300–$400</td></tr>
              </tbody>
            </table>
          </div>

          <p className="fees-note fade-in-up">Rush availability is subject to clinical capacity.</p>
        </div>
      </section>

      {/* Section 7: What We Don't Provide */}
      <section className="fees-section fees-section-alt">
        <div className="container">
          <h2 className="fees-section-heading fade-in-up">A Note on Scope</h2>
          <p className="fees-section-intro fade-in-up">To maintain ethical boundaries and clinical neutrality, the following are not available:</p>
          <ul className="fees-scope-list fade-in-up">
            <li>Character references</li>
            <li>Custody recommendations</li>
            <li>Letters written to influence legal outcomes</li>
            <li>Retroactive documentation for events not assessed in treatment</li>
            <li>Documentation for individuals not actively engaged in therapy</li>
          </ul>
        </div>
      </section>

      {/* Section 8: How to Request */}
      <section className="fees-section fees-cta-section">
        <div className="container">
          <div className="fees-cta-content fade-in-up">
            <h2 className="fees-section-heading">Ready to Request?</h2>
            <p className="fees-cta-body">
              You can submit documentation requests through the secure client portal or by emailing the office directly. Please select your turnaround tier when requesting so we can plan accordingly. All fees are due in full before documentation is released.
            </p>
            <a 
              href="mailto:amirabile@inspiretherapycollective.com" 
              className="btn btn-primary"
              data-testid="fees-contact-btn"
            >
              Contact the Office
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeesDocumentation;
